import React from "react";

import { AttendanceComplianceBar } from "@components/QontactPoints/Fichajes/AttendanceComplianceBar";
import type { QontactPointInformation } from "@models/QontactPoint";
import { HollowedButton, IconButton } from "@shared/designSystem/Button";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import { HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import StatCard from "@shared/designSystem/StatCard";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import { SmallText, Text } from "@shared/designSystem/Texts";
import { H3Title } from "@shared/designSystem/Titles";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { useQontactPointFichajesTabViewModel } from "./QontactPointFichajesTabViewModel";

interface QontactPointFichajesTabProps {
    orgID: string;
    qontactPoint: QontactPointInformation;
    shouldShowMonthlyAttendanceSheetExport?: boolean;
    /** Opens the Turnos tab; omitted when that tab isn't available (e.g. Lite) */
    onOpenShifts?: () => void;
}

/**
 * Fichajes tab of the qontact point detail (design 2c): month selector, exports,
 * StatCards, stacked compliance bar and a notice when check-ins don't match any shift.
 */
const QontactPointFichajesTab: React.FC<QontactPointFichajesTabProps> = ({
    orgID,
    qontactPoint,
    shouldShowMonthlyAttendanceSheetExport = false,
    onOpenShifts,
}) => {
    const viewModel = useQontactPointFichajesTabViewModel(orgID, qontactPoint);
    const { summary, loading } = viewModel;

    const statCardStyle: React.CSSProperties = { flex: "1 1 200px", minWidth: 0 };

    const withoutShiftNotice =
        summary.withoutShift === 1
            ? "1 fichaje no coincide con ningún turno".localized()
            : "%s fichajes no coinciden con ningún turno".localized(String(summary.withoutShift));

    return (
        <VerticalContainer width="100%" height="100%" minHeight="0" scrollable={true} gap={designSystemStyles.gapLg} padding={designSystemStyles.paddingLg} margin="0">
            <HorizontalContainer width="100%" justifyContent="space-between" flexWrap="wrap" gap={designSystemStyles.gapSm}>
                <H3Title text={"Fichajes".localized()} />
                <HorizontalContainer width="auto" gap={designSystemStyles.gapSm} flexWrap="wrap">
                    {/* Previous month: same chevron, turned around */}
                    <div style={{ transform: "rotate(180deg)" }}>
                        <IconButton
                            iconType="chevron"
                            tooltip={"Mes anterior".localized()}
                            onClick={viewModel.previousMonth}
                            disabled={loading}
                        />
                    </div>
                    <HorizontalContainer width="auto" gap={designSystemStyles.gapXsm} minWidth="150px" justifyContent="center">
                        <SystemIcon type="calendar" size="small" color={designSystemStyles.colorText} />
                        <Text text={viewModel.monthLabel} />
                    </HorizontalContainer>
                    <IconButton
                        iconType="chevron"
                        tooltip={"Mes siguiente".localized()}
                        onClick={viewModel.nextMonth}
                        disabled={loading || viewModel.isCurrentMonth}
                    />
                    {shouldShowMonthlyAttendanceSheetExport && (
                        <HollowedButton
                            text={"Exportar planilla mensual".localized()}
                            onClick={viewModel.exportMonthlyAttendanceSheet}
                            disabled={loading || viewModel.isExporting}
                            width="240px"
                        />
                    )}
                    <HollowedButton
                        text={"Exportar datos".localized()}
                        onClick={viewModel.exportData}
                        disabled={loading || viewModel.isExporting}
                        loading={viewModel.isExporting}
                        width="180px"
                    />
                </HorizontalContainer>
            </HorizontalContainer>

            <div style={{ display: "flex", flexWrap: "wrap", gap: designSystemStyles.gapMd, width: "100%" }}>
                <div style={statCardStyle}>
                    <StatCard label={"Total".localized()} count={summary.total} selected={true} loading={loading} width="100%" />
                </div>
                <div style={statCardStyle}>
                    <StatCard
                        label={"A tiempo".localized()}
                        count={summary.onTime}
                        color={designSystemStyles.colorStatusSuccess}
                        loading={loading}
                        width="100%"
                    />
                </div>
                <div style={statCardStyle}>
                    <StatCard
                        label={"Tarde o salida temprana".localized()}
                        count={summary.late}
                        color={designSystemStyles.colorStatusError}
                        loading={loading}
                        width="100%"
                    />
                </div>
                <div style={statCardStyle}>
                    <StatCard
                        label={"Sin turno".localized()}
                        count={summary.withoutShift}
                        color={designSystemStyles.colorStatusVoided}
                        loading={loading}
                        width="100%"
                    />
                </div>
            </div>

            {!loading && summary.total === 0 && (
                <SmallText
                    text={"No hay fichajes en %s.".localized(viewModel.monthLabel)}
                    color={designSystemStyles.colorTextSecondary}
                />
            )}

            {!loading && (
                <AttendanceComplianceBar
                    segments={[
                        { id: "onTime", label: "a tiempo".localized(), count: summary.onTime, color: designSystemStyles.colorStatusSuccess },
                        { id: "late", label: "tarde o salida temprana".localized(), count: summary.late, color: designSystemStyles.colorStatusError },
                        { id: "withoutShift", label: "sin turno".localized(), count: summary.withoutShift, color: designSystemStyles.colorStatusVoided },
                    ]}
                />
            )}

            {!loading && summary.withoutShift > 0 && (
                <SimpleCardContainer variant="inlineSetup">
                    <HorizontalContainer width="100%" alignment="flex-start" gap={designSystemStyles.gapSm}>
                        <SystemIcon type="info" color={designSystemStyles.colorStatusInfo} />
                        <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm} margin="0">
                            <Text text={`**${withoutShiftNotice}**`} markdown={true} />
                            <SmallText
                                text={"Puede faltar un turno o un día en la configuración de este lugar.".localized()}
                                color={designSystemStyles.colorTextSupporting}
                            />
                            {onOpenShifts && (
                                <ClickableWrapper onClick={onOpenShifts}>
                                    <SmallText text={"**Revisar turnos**".localized()} markdown={true} color={designSystemStyles.colorMainAction} />
                                </ClickableWrapper>
                            )}
                        </VerticalContainer>
                    </HorizontalContainer>
                </SimpleCardContainer>
            )}
        </VerticalContainer>
    );
};

export default QontactPointFichajesTab;
